import useFeeTierModal from 'src/components/Modals/FeeTierModal/hooks/useFeeTierModal';
import { FEE_TIER_DISPLAY_UNIT_OPTIONS } from 'src/components/Modals/FeeTierModal/consts';
import FeeTierModalProgressBar from 'src/components/Modals/FeeTierModal/FeeTierModalProgressBar';
import FeeTierModalTable from 'src/components/Modals/FeeTierModal/FeeTierModalTable/FeeTierModalTable';
import DelegateWalletMessage from 'src/components/DelegateWalletMessage/DelegateWalletMessage';
import Divider from 'src/components/primitives/Divider/Divider';
import RadioGroup from 'src/components/primitives/RadioGroup/RadioGroup';
import ResponsiveModal from 'src/components/Modals/ResponsiveModal/ResponsiveModal';
import Button from 'src/components/primitives/Button/Button';

export default function FeeTierModal() {
	const {
		feeTiers,
		userFeeTierIndex,
		isPromoLiftingUserTier,
		userCurrentVolume,
		userNextTierVolume,
		showNextVolumeTier,
		displayUnit,
		progressBarPercentage,
		tableRows,
		takerFeeGroups,
		userNextTierLabel,
		handleChangeDisplayUnit,
		handleCloseModal,
	} = useFeeTierModal();

	if (!feeTiers) {
		return null;
	}

	// The columns only differ once a surcharge is set on one side.
	const hasMarketSpecificTakerFees = takerFeeGroups.some(
		(group) => group.addonTenthBps !== takerFeeGroups[0]?.addonTenthBps
	);

	return (
		<ResponsiveModal.Container
			onClose={handleCloseModal}
			className="sm:w-[560px]"
			dataTestId="fee-tier-modal"
		>
			<ResponsiveModal.Title closeButtonTestId="fee-tier-modal-close">
				Fee table
			</ResponsiveModal.Title>
			<ResponsiveModal.Body className="max-h-none">
				<div className="flex flex-col gap-y-lg">
					<DelegateWalletMessage inherited="fee tier" />
					<div className="flex flex-col gap-y-xs">
						<div className="flex items-center justify-between typo-body-xs text-text-default">
							<span>30D volume (rolling est.)</span>
							{showNextVolumeTier && <span>Next: {userNextTierLabel}</span>}
						</div>
						<div className="flex items-center justify-between typo-heading-h4 text-text-default">
							<span>{userCurrentVolume}</span>
							{showNextVolumeTier && <span>{userNextTierVolume}</span>}
						</div>
					</div>
					<FeeTierModalProgressBar
						progressBarPercentage={progressBarPercentage}
					/>
				</div>
				<Divider />
				<div className="flex flex-col gap-y-md">
					<div className="flex items-center justify-between">
						<span className="typo-body-xs text-text-default">Fee Tiers</span>
						<RadioGroup.Default
							size="md"
							variant="card"
							items={FEE_TIER_DISPLAY_UNIT_OPTIONS}
							value={displayUnit}
							onValueChange={handleChangeDisplayUnit}
						/>
					</div>
					<FeeTierModalTable
						tableRows={tableRows}
						takerFeeGroups={takerFeeGroups}
						userFeeTierIndex={userFeeTierIndex}
						isPromoLiftingUserTier={isPromoLiftingUserTier}
					/>
					<span className="typo-body-2xs text-text-secondary">
						{/* Nothing left to progress toward once the charged tier is the
						top rung, whether it was earned or floored by a promo. */}
						{showNextVolumeTier &&
							'Progress to the next fee tier by satisfying the rolling 30D volume requirement. '}
						Negative fees represent rebates.
						{hasMarketSpecificTakerFees &&
							' Taker fees vary by market; maker rebates do not.'}
					</span>
				</div>
			</ResponsiveModal.Body>
			<ResponsiveModal.Footer className="sm:hidden">
				{({ close }) => (
					<Button
						size="lg"
						className="w-full"
						variant="primary"
						onClick={close}
						dataTestId="fee-tier-modal-close-button"
					>
						Close
					</Button>
				)}
			</ResponsiveModal.Footer>
		</ResponsiveModal.Container>
	);
}
